import copy from 'copy-to-clipboard';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';

import {
  AvatarNode,
  Card,
  CountDownText,
  FallbackActionGroup,
  InvitationStepsButtonGroup,
  PostView,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
} from '../../../components/Molecules';
import { DEFAULT_APP_STORE_URL } from '../../../constants/appInfo';
import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { Icons, IconType } from '../../../constants/icons';
import { getInvitationMessage } from '../../../constants/shareInvitationLink';
import { SpaceMembershipRole } from '../../../constants/space';
import { Theme } from '../../../constants/Theme';
import { emailSchema } from '../../../constants/validationSchemas';
import useDeepLink from '../../../hooks/useDeeplink';
import { TranslationLocale } from '../../../i18n';
import {
  Audience,
  PostContentType,
  PostState,
} from '../../../models/PostStore/Post';
import { RegisterResult } from '../../../models/SessionStore';
import { SpaceMembershipState } from '../../../models/SpaceStore';
import { ProfileMapType } from '../../../models/UserStore';
import {
  InvitationLink,
  UserInvitationType,
} from '../../../models/UserStore/Invitation';
import { usePostStore } from '../../../stores/PostStore';
import { useSessionStore } from '../../../stores/SessionStore';
import { useSpaceStore } from '../../../stores/SpaceStore';
import { useUserStore } from '../../../stores/UserStore';
import { getUiLocale } from '../../../utils/i18n';

const { register } = useSessionStore.getState();
const { fetchPost } = usePostStore.getState();

type PostSharingTemplateProps = {
  invitationLink: InvitationLink;
};

export const PostSharingScreen = (props: PostSharingTemplateProps) => {
  const { invitationLink } = props;
  const [currentStep, setCurrentStep] = useState<
    'info' | 'register' | 'result'
  >('info');
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loaded',
  );
  const { t, i18n } = useTranslation();

  const contentInviterUserId = invitationLink.userId;
  const contentInviterProfile = useUserStore

    .getState()
    .getProfile(contentInviterUserId);

  const postId =
    invitationLink?.type == UserInvitationType.PostSharing &&
    'postId' in invitationLink.content
      ? invitationLink.content.postId
      : undefined;
  useEffect(() => {
    if (postId) {
      const loadPostAndMembership = async () => {
        try {
          setStatus('loading');
          await fetchPost(postId);
        } catch (error) {
          setStatus('error');
          console.error(error);
        } finally {
          setStatus('loaded');
        }
      };

      loadPostAndMembership().catch(console.error);
    }
  }, [postId]);

  const post = postId ? usePostStore.getState().getPost(postId) : undefined;

  const creatorUserId = post?.createdBy ?? NOT_APPLICABLE_STRING;
  const creatorUserProfile = useUserStore.getState().getProfile(creatorUserId);

  const spaceId = post?.channel.id ?? NOT_APPLICABLE_STRING;
  const classroom = useSpaceStore((store) => {
    return store.getSpace(spaceId);
  }, shallow);
  // album
  const postAlbum = useSpaceStore((store) => {
    return store.getAlbum(
      post?.content.type == PostContentType.Album
        ? post.content.gameSetId
        : NOT_APPLICABLE_STRING,
    );
  }, shallow);
  const albumClassroom = useSpaceStore((store) => {
    return store.getSpace(postAlbum?.classroomId ?? NOT_APPLICABLE_STRING);
  }, shallow);

  const postSharingClassroomId =
    post?.content.type == PostContentType.Space
      ? post.content.classroomId
      : NOT_APPLICABLE_STRING;

  const postSharingClassroom = useSpaceStore((store) => {
    const sharingClassroom = store.getSpace(postSharingClassroomId);

    return sharingClassroom;
  }, shallow);

  const postSharingSpaceOwnerProfile = useUserStore((store) => {
    const profile = store.getProfileFromMap(
      ProfileMapType.SpaceRoot,
      postSharingClassroomId,
    );
    return profile;
  }, shallow);

  // multiplayerGame
  const postMultiplayerGame = useSpaceStore((store) => {
    return store.getMultiplayerGame(
      post?.content.type == PostContentType.MultiplayerGame
        ? post.content.multiplayerGameId
        : NOT_APPLICABLE_STRING,
    );
  }, shallow);

  //shareUser
  const sharingUserId =
    post?.content.type == PostContentType.User
      ? post.content.userId
      : NOT_APPLICABLE_STRING;
  const sharingUser = useUserStore.getState().getProfile(sharingUserId);

  // membership
  const currentUserId =
    useSessionStore.getState().activeUserId ?? NOT_APPLICABLE_STRING;
  const membership = useSpaceStore(
    (state) => state.getMembership(spaceId, currentUserId),
    shallow,
  );
  // post visibility
  const isPostAvailable = post?.state == PostState.Available;
  const isPublic = post?.audience == Audience.Public;
  const isMemberOnly =
    post?.audience == Audience.Members &&
    membership?.state == SpaceMembershipState.InGroup;
  const isTierContent =
    Array.isArray(post?.audience) &&
    membership?.tier?.tierId &&
    post?.audience.includes(membership.tier.tierId);
  const isRootAdmin =
    membership?.role !== undefined &&
    [SpaceMembershipRole.Admin, SpaceMembershipRole.Root].includes(
      membership.role,
    );
  const allowDisplay =
    isPostAvailable &&
    (isPublic || isMemberOnly || isTierContent || isRootAdmin);

  const [form, setForm] = useState<{
    email: string;
    password?: string;
    agreeStatement: boolean;
    emailErrorMessage?: string;
  }>({
    email: invitationLink?.email ?? '',
    password: undefined,
    agreeStatement: false,
  });
  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const validateResult = emailSchema.safeParse(event.target.value);

    setForm((s) => ({
      ...s,
      email: event.target.value,
      emailErrorMessage: validateResult.success
        ? undefined
        : 'Please enter a valid email address.',
    }));
  }, []);

  const expiryAt = invitationLink.expiredAt;

  const submit = useCallback(async () => {
    try {
      setStatus('loading');
      const response = await register({
        uiTheme: Theme.Auto,
        interfaceLocale: getUiLocale(i18n.language as TranslationLocale),
        email: form.email,
        userCode: invitationLink?.userCode,
      });

      if (response.result == RegisterResult.Success) {
        const password = response.password;
        if (password) {
          setForm((s) => ({ ...s, password: password }));
        }
        setStatus('loaded');
        setCurrentStep('result');
      } else {
        toast.error(t('There is something wrong.'));
        setStatus('error');
      }
    } catch (error) {
      console.error(error);
    }
  }, [form.email, i18n.language, invitationLink?.userCode, t]);

  const onClick = () => {
    submit().catch((error) => {
      console.error(error);
    });
  };

  const onPressCopy = () => {
    copy(form.password ?? '');
    toast.success(t('Password is copied.'));
  };
  const onPresOpenAppElseAppStore = useDeepLink(
    `joyajoyapp://user-invite?invitationLinkId=${invitationLink.invitationLinkId}`,
  );

  if (status == 'loading')
    return (
      <>
        <div
          className="flex flex-col justify-center items-center"
          style={{ flex: 1 }}
        >
          <Spinner
            animation="border"
            role="status"
            style={{ color: '#05656B' }}
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </>
    );

  if (currentStep == 'info') {
    return (
      <>
        <div
          className="flex flex-col share-content"
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
          }}
        >
          <Card style={{ width: '100%', maxWidth: '480px' }}>
            {contentInviterProfile ? (
              <>
                <AvatarNode
                  avatar={{
                    ...contentInviterProfile.avatar,
                    userId: contentInviterProfile.userId,
                    source:
                      contentInviterProfile.profilePicture &&
                      contentInviterProfile.profilePicture.publicUrls?.small
                        ? {
                            uri: contentInviterProfile.profilePicture.publicUrls
                              .small,
                          }
                        : NOT_APPLICABLE_STRING,
                  }}
                  size={80}
                />
                <p
                  className="text-lg text-cyan-800"
                  style={{ textAlign: 'center', padding: '1rem' }}
                >
                  <span className="font-bold">
                    {contentInviterProfile.name}{' '}
                  </span>
                  {t(getInvitationMessage(UserInvitationType.PostSharing))}
                </p>
              </>
            ) : null}

            <>
              {allowDisplay ? (
                <>
                  <PostView
                    post={post}
                    creator={creatorUserProfile}
                    classroom={classroom}
                    album={postAlbum}
                    albumClassroom={albumClassroom}
                    membership={membership}
                    //
                    sharingSpace={postSharingClassroom}
                    sharingSpaceOwner={postSharingSpaceOwnerProfile}
                    //
                    sharingUser={sharingUser}
                    //
                    sharingMultiplayerGame={postMultiplayerGame}
                  />
                </>
              ) : (
                <div className="post unselectable">
                  <div
                    className="flex flex-col gap-1 bg-gray-300"
                    style={{
                      padding: '1rem',
                      margin: '1rem',
                      borderRadius: '1rem',
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <img
                        className="size-icon-m"
                        src={Icons[IconType.ReportProblem]}
                      />
                      <p className="text-md">
                        {t(
                          'Sorry, this content isn’t available at the moment.',
                        )}
                      </p>
                    </div>

                    <p className="text-gray-600 text-sm">
                      {t(
                        'When this happens, it’s usually because the owner only shared it with a small group of people, changed who can see or it’s been deleted.',
                      )}
                    </p>
                  </div>
                </div>
              )}
            </>

            <div
              className="flex flex-col items-center w-full"
              style={{ padding: '0.25rem', marginTop: '16px' }}
            >
              {/* <p className="text-lg font-bold">
                {t('Click OK to view details')}
              </p>
              <TextButton
                text={t('OK')}
                size={TextButtonSize.Large}
                variant={TextButtonVariant.Cyan}
                onClick={onPresOpenAppElseAppStore}
              /> */}
              <InvitationStepsButtonGroup
                onPressAccept={onPresOpenAppElseAppStore}
              />

              <CountDownText
                timestamp={expiryAt.getTime()}
                label={t('Expiry: ')}
                className="text-sm font-bold text-red-550"
              />
              <FallbackActionGroup />
            </div>
          </Card>
        </div>
      </>
    );
  }
  if (currentStep == 'register') {
    return (
      <div
        className="flex flex-col"
        style={{ flex: 1, backgroundColor: '#EDEDED' }}
      >
        <section
          className="flex flex-col items-center justify-center p-3"
          style={{ gap: '1rem' }}
        >
          <img
            className="classroom-icon size-icon-2xl"
            src={Icons[IconType.PostLine]}
          />
          <p className="text-2xl font-bold">{t('View Post')}</p>

          <>
            {allowDisplay ? (
              <>
                <PostView
                  post={post}
                  creator={creatorUserProfile}
                  classroom={classroom}
                  album={postAlbum}
                  albumClassroom={albumClassroom}
                  membership={membership}
                  //
                  sharingSpace={postSharingClassroom}
                  sharingSpaceOwner={postSharingSpaceOwnerProfile}
                  //
                  sharingUser={sharingUser}
                  //
                  sharingMultiplayerGame={postMultiplayerGame}
                  backgroundColor={'#ffffff'}
                />
              </>
            ) : (
              <div className="post unselectable">
                <div
                  className="flex flex-col gap-1 bg-gray-300"
                  style={{
                    padding: '1rem',
                    margin: '1rem',
                    borderRadius: '1rem',
                  }}
                >
                  <div className="flex items-center gap-1">
                    <img
                      className="size-icon-m"
                      src={Icons[IconType.ReportProblem]}
                    />
                    <p className="text-md">
                      {t('Sorry, this content isn’t available at the moment.')}
                    </p>
                  </div>

                  <p className="text-gray-600 text-sm">
                    {t(
                      'When this happens, it’s usually because the owner only shared it with a small group of people, changed who can see or it’s been deleted.',
                    )}
                  </p>
                </div>
              </div>
            )}
          </>

          <p
            style={{
              textAlign: 'center',
              padding: '0rem 1.5rem',
              maxWidth: '600px',
            }}
          >
            {t(
              'If there is no JOYAJOY account with the following email, a new account will be created for you.',
            )}
          </p>
          <TextInput
            value={form.email}
            placeholder={t('Email')}
            onChange={onEmailChange}
            errorMessage={form.emailErrorMessage}
          />
          <div className="flex flex-row gap-2 items-center">
            <input
              type="checkbox"
              id="accept-policy"
              checked={form.agreeStatement}
              onChange={() =>
                setForm((s) => ({ ...s, agreeStatement: !s.agreeStatement }))
              }
            />
            <div>
              <label htmlFor="accept-policy">
                <Trans i18nKey="Accept Terms">
                  I agree to JOYAJOY&apos;s
                  <a href="https://joyajoy.com/zh/pages/term-of-services">
                    Terms and Conditions
                  </a>
                  and
                  <a href="https://joyajoy.com/zh/pages/privacy-policy">
                    Privacy Statement
                  </a>
                </Trans>
              </label>
            </div>
          </div>
          <hr />
          <p className="text-xl">{t('View Post now!')}</p>
          <TextButton
            text={t('View')}
            size={TextButtonSize.Large}
            variant={TextButtonVariant.Cyan}
            onClick={onClick}
            disabled={
              form.agreeStatement == false ||
              form.emailErrorMessage != undefined
            }
          />
          <CountDownText
            timestamp={expiryAt.getTime()}
            label={t('Expiry: ')}
            className="text-sm font-bold text-red-550"
          />
        </section>
      </div>
    );
  }
  if (currentStep == 'result') {
    return (
      <div
        className="flex flex-col"
        style={{ flex: 1, backgroundColor: '#EDEDED' }}
      >
        <section
          className="flex flex-col items-center justify-center p-3"
          style={{ gap: '1rem' }}
        >
          <img
            className="classroom-icon size-icon-2xl"
            src={Icons[IconType.Tick]}
          />
          <p className="text-2xl font-bold">{t('Invitation to View Post')}</p>

          <>
            {allowDisplay ? (
              <>
                <PostView
                  post={post}
                  creator={creatorUserProfile}
                  classroom={classroom}
                  album={postAlbum}
                  albumClassroom={albumClassroom}
                  membership={membership}
                  //
                  sharingSpace={postSharingClassroom}
                  sharingSpaceOwner={postSharingSpaceOwnerProfile}
                  //
                  sharingUser={sharingUser}
                  //
                  sharingMultiplayerGame={postMultiplayerGame}
                  backgroundColor={'#ffffff'}
                />
              </>
            ) : (
              <div className="post unselectable">
                <div
                  className="flex flex-col gap-1 bg-gray-300"
                  style={{
                    padding: '1rem',
                    margin: '1rem',
                    borderRadius: '1rem',
                  }}
                >
                  <div className="flex items-center gap-1">
                    <img
                      className="size-icon-m"
                      src={Icons[IconType.ReportProblem]}
                    />
                    <p className="text-md">
                      {t('Sorry, this content isn’t available at the moment.')}
                    </p>
                  </div>

                  <p className="text-gray-600 text-sm">
                    {t(
                      'When this happens, it’s usually because the owner only shared it with a small group of people, changed who can see or it’s been deleted.',
                    )}
                  </p>
                </div>
              </div>
            )}
          </>

          <p
            style={{
              textAlign: 'center',
              padding: '0rem 1.5rem',
              maxWidth: '600px',
            }}
          >
            {t(
              'You are invited to view the Post with the following email. A JOYAJOY account has been created for you!',
            )}
          </p>
          <div
            className="rounded-lg"
            style={{
              overflow: 'auto',
              background: 'white',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <div className="flex flex-row items-center gap-1">
              <img className="size-icon-m" src={Icons[IconType.Email]} />
              <p>{t('Email: {email}', { email: form.email })}</p>
            </div>
            {form.password ? (
              <div className="flex flex-row items-center gap-1">
                <img className="size-icon-m" src={Icons[IconType.Password]} />
                <p>{t('Password: {password}', { password: form.password })}</p>
                <img
                  onClick={onPressCopy}
                  className="size-icon-s icon-button"
                  src={Icons[IconType.Copy]}
                />
              </div>
            ) : null}
          </div>
          <TextButton
            text={t('OK')}
            size={TextButtonSize.Small}
            variant={TextButtonVariant.Cyan}
            onClick={() => {
              window.open(
                DEFAULT_APP_STORE_URL,
                '_blank',
                'noopener,noreferrer',
              );
            }}
          />
        </section>
      </div>
    );
  }
  return <></>;
};
